// src/services/AlertService.ts

import { UnifiedPatient } from '../types/unified-financial';

export interface PackageAlert {
  patientId: string;
  patientName: string;
  packageName: string;
  daysUntilExpiry: number;
  sessionsRemaining: number;
  totalSessions: number;
  sessionsUsed: number;
  expiryDate: string;
  alertType: 'warning' | 'critical' | 'expired';
}

export class AlertService {
  
  /**
   * Calcula alertas de vencimento para todos os pacientes
   */
  static calculatePackageAlerts(patients: UnifiedPatient[]): PackageAlert[] {
    const alerts: PackageAlert[] = [];
    const today = new Date();

    for (const patient of patients) {
      // Só processar pacientes ativos com pacote
      if (patient.financialProfile?.active !== true || !patient.financialProfile?.hasPackage || !patient.financialProfile?.packageInfo) {
        continue;
      }

      const packageInfo: any = patient.financialProfile.packageInfo;
      
      // Calcular data de expiração
      const paymentDateRaw = packageInfo.packagePaymentDate || packageInfo.paymentDate;
      const paymentDate = paymentDateRaw ? new Date(paymentDateRaw) : new Date();
      if (Number.isNaN(paymentDate.getTime())) {
        continue;
      }
      const expiryDate = new Date(paymentDate);

      const validityType = packageInfo.packageValidityType || packageInfo.validityType || '1_MONTH';
      let monthsToAdd = 1;
      if (validityType === '2_MONTHS') monthsToAdd = 2;
      else if (validityType === '3_MONTHS') monthsToAdd = 3;
      else if (validityType === 'CUSTOM' && (packageInfo.packageCustomValidityMonths || packageInfo.customValidityMonths)) {
        monthsToAdd = packageInfo.packageCustomValidityMonths || packageInfo.customValidityMonths;
      }
      expiryDate.setMonth(expiryDate.getMonth() + monthsToAdd);

      // Calcular dias até expiração
      const timeDiff = expiryDate.getTime() - today.getTime();
      const daysUntilExpiry = Math.ceil(timeDiff / (1000 * 3600 * 24));

      const ensureNumber = (value: any): number | undefined => {
        if (typeof value === 'number' && !Number.isNaN(value)) return value;
        return undefined;
      };

      const legacyPackage = patient.financialProfile.package;

      const sessionsContracted =
        ensureNumber(legacyPackage?.sessionsContracted) ??
        ensureNumber(packageInfo.packageSessionsContracted) ??
        ensureNumber(packageInfo.sessionsContracted) ??
        0;

      const summary: any = (patient as any).appointmentSummary;

      const summaryUsed =
        summary && typeof summary.completed === 'number' ? summary.completed : undefined;
      const summaryRemaining =
        summary && typeof summary.total === 'number'
          ? Math.max(0, sessionsContracted - summary.completed)
          : undefined;

      const rawSessionsUsed =
        ensureNumber(legacyPackage?.sessionsUsed) ??
        ensureNumber(packageInfo.packageSessionsUsed) ??
        ensureNumber(packageInfo.sessionsUsed) ??
        ensureNumber(patient.financialProfile.currentCycle?.sessionsUsed);

      const rawSessionsRemaining =
        ensureNumber(legacyPackage?.sessionsRemaining) ??
        ensureNumber(packageInfo.packageSessionsRemaining) ??
        ensureNumber(packageInfo.sessionsRemaining) ??
        ensureNumber(patient.financialProfile.currentCycle?.sessionsRemaining);

      const sessionsUsed = Math.max(
        0,
        summaryUsed ??
          (rawSessionsUsed != null
            ? rawSessionsUsed
            : rawSessionsRemaining != null
            ? sessionsContracted - rawSessionsRemaining
            : 0)
      );

      const sessionsRemaining =
        summaryRemaining ??
        (rawSessionsRemaining != null
          ? Math.max(0, rawSessionsRemaining)
          : Math.max(0, sessionsContracted - sessionsUsed));

      // Determinar tipo de alerta
      let alertType: 'warning' | 'critical' | 'expired' = 'warning';
      
      if (daysUntilExpiry < 0) {
        alertType = 'expired';
      } else if (daysUntilExpiry <= 3) {
        alertType = 'critical';
      } else if (daysUntilExpiry <= 7) {
        alertType = 'warning';
      } else {
        // Não criar alerta se ainda tem mais de 7 dias
        continue;
      }

      alerts.push({
        patientId: patient.id,
        patientName: patient.personalData.name,
        packageName: packageInfo.packageName || packageInfo.name || 'Pacote',
        daysUntilExpiry,
        sessionsRemaining,
        totalSessions: sessionsContracted,
        sessionsUsed,
        expiryDate: expiryDate.toISOString().split('T')[0],
        alertType
      });
    }

    // Ordenar por urgência (expirados primeiro, depois críticos, depois warnings)
    return alerts.sort((a, b) => {
      const typeOrder = { expired: 0, critical: 1, warning: 2 };
      if (typeOrder[a.alertType] !== typeOrder[b.alertType]) {
        return typeOrder[a.alertType] - typeOrder[b.alertType];
      }
      return a.daysUntilExpiry - b.daysUntilExpiry;
    });
  }

  /**
   * Filtra alertas por tipo
   */
  static filterAlertsByType(alerts: PackageAlert[], type: 'warning' | 'critical' | 'expired'): PackageAlert[] {
    return alerts.filter(alert => alert.alertType === type);
  }

  /**
   * Conta alertas por tipo
   */
  static countAlertsByType(alerts: PackageAlert[]): { warning: number; critical: number; expired: number } {
    return {
      warning: alerts.filter(a => a.alertType === 'warning').length,
      critical: alerts.filter(a => a.alertType === 'critical').length,
      expired: alerts.filter(a => a.alertType === 'expired').length
    };
  }

  /**
   * Gera mensagem de alerta formatada
   */
  static formatAlertMessage(alert: PackageAlert): string {
    if (alert.alertType === 'expired') {
      return `Pacote de ${alert.patientName} EXPIROU há ${Math.abs(alert.daysUntilExpiry)} dias`;
    } else if (alert.alertType === 'critical') {
      return `Pacote de ${alert.patientName} expira em ${alert.daysUntilExpiry} dias`;
    } else {
      return `Pacote de ${alert.patientName} expira em ${alert.daysUntilExpiry} dias`;
    }
  }
}
